/**
 * 「安装到主屏幕」（Phase 3d）。
 *
 * Chrome 系在满足条件时发一次 `beforeinstallprompt`，而且往往在设置页打开之前就发了 ——
 * 所以启动时就接住存起来，设置页再从这里取。iOS 没有这个事件，只能教人去分享菜单里加。
 */

import { useSyncExternalStore } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallState =
  /** 已经是以 app 的方式打开的 */
  | 'installed'
  /** 浏览器允许我们弹安装框 */
  | 'promptable'
  /** iOS Safari：只能手动在分享菜单里加 */
  | 'ios'
  /** 其余情况：浏览器菜单里自己找 */
  | 'manual'

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    Boolean(nav.standalone) ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  )
}

function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
}

/** 在 main.tsx 里调一次 */
export function initInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // 拦下浏览器自己的小横条，换成设置页里的按钮
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    installed = true
    notify()
  })
}

export function installState(): InstallState {
  if (installed || isStandalone()) return 'installed'
  if (deferred) return 'promptable'
  if (isIos()) return 'ios'
  return 'manual'
}

/** 弹出浏览器的安装框。返回用户是否装了 */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  const event = deferred
  deferred = null
  await event.prompt()
  const { outcome } = await event.userChoice
  notify()
  return outcome === 'accepted'
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    installState,
    () => 'manual',
  )
}
